import { useState, useEffect } from 'react'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Dialog from './Dialog'
import InputField from './InputField'
import AutoComplete from './AutoComplete'
import GridIconButton from './GridIconButton'
import SortableTableHead from './SortableTableHead'
import { titlesImpostazioni } from '../settings/settings'
import { formatBodyField } from '../hooks/utils-functions'
import {
  useGetZoneQuery,
  useCreateZonaMutation,
  useUpdateZonaMutation,
  useDeleteZonaMutation,
} from '../hooks/api-slice'

const columnsZone = [
  { label: 'Zona', sortable: false },
  { label: 'Zone vicine', sortable: false },
  { label: 'Descrizione', sortable: false },
  { label: 'Opzioni', sortable: false },
]

const DialogZone = ({ open, handleClose }) => {
  const { data: zone } = useGetZoneQuery()

  const [createZonaMutation] = useCreateZonaMutation()
  const [updateZonaMutation] = useUpdateZonaMutation()
  const [deleteZonaMutation] = useDeleteZonaMutation()

  const [editingZona, setEditingZona] = useState()
  const [addingZona, setAddingZona] = useState()
  const [newZona, setNewZona] = useState()

  const cSetNewZona = (field, value) =>
    setNewZona({ ...newZona, [field]: formatBodyField(value) })

  const clearNewZona = () => {
    setEditingZona()
    setAddingZona()
    setNewZona()
  }

  const addNewZona = async () => {
    const result = await createZonaMutation(newZona)
    if (!result?.error) clearNewZona()
  }

  const editZona = async id => {
    const result = await updateZonaMutation({ id, ...newZona })
    if (!result?.error) clearNewZona()
  }

  const deleteZona = async id => {
    await deleteZonaMutation(id)
  }

  useEffect(() => {
    clearNewZona()
  }, [open])

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleZone}
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          Le zone servono all'algoritmo Gironi per fare gironi geograficamente
          vicini. A ciascuna società può essere assegnata una zona di
          appartenenza.
        </Grid>
        {!zone ? (
          <h4>Zone non trovate.</h4>
        ) : (
          <>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ width: '100%' }}
            >
              {(!!addingZona && (
                <>
                  <Grid size={{ xs: 4, md: 2 }}>
                    <center>
                      <InputField
                        id='zona'
                        value={newZona?.zona}
                        onChange={i => cSetNewZona('zona', i)}
                        autoFocus
                        label={columnsZone[0].label}
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 8, md: 4 }}>
                    <center>
                      <AutoComplete
                        id='vicini'
                        value={zone?.filter(z =>
                          newZona?.vicini?.includes(z.id),
                        )}
                        options={zone?.filter(i => i.zona !== newZona?.zona)}
                        blurOnSelect={false}
                        multiple
                        label={columnsZone[1].label}
                        getOptionLabel={op => op.zona}
                        onChange={i =>
                          cSetNewZona('vicini', i.map(z => z.id) || [])
                        }
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 8, md: 4 }}>
                    <center>
                      <InputField
                        id='descrizione'
                        value={newZona?.descrizione}
                        onChange={i => cSetNewZona('descrizione', i)}
                        label={columnsZone[2].label}
                      />
                    </center>
                  </Grid>
                  <GridIconButton
                    type='save'
                    size={{ xs: 2, md: 1 }}
                    onClick={addNewZona}
                  />
                  <GridIconButton
                    type='clear'
                    size={{ xs: 2, md: 1 }}
                    onClick={clearNewZona}
                  />
                </>
              )) || (
                <Grid size={12}>
                  <center>
                    <GridIconButton
                      type='add'
                      size={12}
                      onClick={() => {
                        setEditingZona()
                        setAddingZona(true)
                        setNewZona({})
                      }}
                      text='Nuova zona'
                    />
                  </center>
                </Grid>
              )}
            </Grid>
            {(zone.length === 0 && <h4>Nessuna zona trovata.</h4>) || (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead table={zone} columns={columnsZone} />
                  <TableBody>
                    {zone.map((z, index) => {
                      return (
                        <TableRow key={index}>
                          {(!!editingZona && newZona?.zona === z.zona && (
                            <>
                              <TableCell padding='checkbox'>{z.zona}</TableCell>
                              <TableCell padding='checkbox'>
                                <AutoComplete
                                  id='vicini'
                                  value={zone?.filter(z =>
                                    newZona?.vicini?.includes(z.id),
                                  )}
                                  options={zone?.filter(
                                    i => i.zona !== newZona?.zona,
                                  )}
                                  blurOnSelect={false}
                                  multiple
                                  label={columnsZone[1].label}
                                  getOptionLabel={op => op.zona}
                                  onChange={i =>
                                    cSetNewZona(
                                      'vicini',
                                      i.map(z => z.id) || [],
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <InputField
                                  id='descrizione'
                                  value={newZona?.descrizione}
                                  onChange={i => cSetNewZona('descrizione', i)}
                                  autoFocus
                                  label={columnsZone[2].label}
                                  mode='update'
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <Grid container sx={{ width: '100%' }}>
                                  <GridIconButton
                                    type='save'
                                    onClick={() => editZona(z.id)}
                                  />
                                  <GridIconButton
                                    type='clear'
                                    onClick={clearNewZona}
                                  />
                                </Grid>
                              </TableCell>
                            </>
                          )) || (
                            <>
                              <TableCell>{z.zona}</TableCell>
                              <TableCell>
                                {zone
                                  .filter(i => z.vicini.includes(i.id))
                                  ?.map(i => i.zona)
                                  .join(', ')}
                              </TableCell>
                              <TableCell>{z.descrizione}</TableCell>
                              <TableCell>
                                <Grid container sx={{ width: '100%' }}>
                                  <GridIconButton
                                    type='edit'
                                    onClick={() => {
                                      setEditingZona(true)
                                      setAddingZona()
                                      setNewZona(z)
                                    }}
                                  />
                                  <GridIconButton
                                    type='delete'
                                    onClick={() => deleteZona(z.id)}
                                  />
                                </Grid>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogZone
